import { useAuth } from "./useAuth";
import { oids } from "../configs/Settings";

export interface QuestionnaireResponseItem {
    linkId: string;
    text?: string;
    answer?: QuestionnaireResponseAnswer[];
    item?: QuestionnaireResponseItem[];
}

export interface QuestionnaireResponseAnswer {
    valueBoolean?: boolean;
    valueDecimal?: number;
    valueInteger?: number;
    valueDate?: string;
    valueDateTime?: string;
    valueTime?: string;
    valueString?: string;
    valueUri?: string;
    valueAttachment?: any;
    valueCoding?: {
        system?: string;
        code: string;
        display?: string;
    };
    valueQuantity?: any;
    valueReference?: any;
}

export interface QuestionnaireResponseData {
    resourceType: "QuestionnaireResponse";
    id?: string;
    meta?: {
        versionId: string;
        lastUpdated: string;
        profile: string[];
        tag?: {
            system: string;
            code: string;
            display: string;
        }[];
    };
    identifier?: {
        system: string;
        value: string;
    }[];
    questionnaire: string;
    status: "in-progress" | "completed" | "amended" | "entered-in-error" | "stopped";
    subject: {
        reference: string;
        display?: string;
    };
    encounter?: {
        reference: string;
        display?: string;
    };
    authored: string;
    author?: {
        reference: string;
        display?: string;
    };
    source?: {
        reference: string;
        display?: string;
    };
    item: QuestionnaireResponseItem[];
}


export async function buildQuestionnaireResponse(
    questionnaireId: string,
    answers: Record<string, any>,
    questionnaire: any,
    patientId?: string,
    encounterId?: string,
    practitionerId?: string,
    practitionerInfo?: { healthcare?: string; practitioner?: string }
): Promise<QuestionnaireResponseData> {
    const now = new Date().toISOString();
    const subjectId = await useAuth.getSubjectId();

    const buildResponseItems = (items: any[]): QuestionnaireResponseItem[] => {
        return items.map((item) => {
            const responseItem: QuestionnaireResponseItem = {
                linkId: item.linkId,
                text: item.text,
            };

            if (item.type === "group" && Array.isArray(item.item)) {
                const childItems = buildResponseItems(item.item);
                if (!childItems || childItems.length === 0) {
                    return null;
                }
                responseItem.item = childItems;
                return responseItem;
            }

            const answer = answers[item.linkId];
            if (answer === undefined || answer === null || answer === "") {
                return null;
            }
            const builtAnswers = buildAnswers(item, answer);
            if (!builtAnswers || builtAnswers.length === 0) {
                return null;
            }
            responseItem.answer = builtAnswers;
            return responseItem;
        }).filter(Boolean) as QuestionnaireResponseItem[];
    };

    const buildAnswers = (item: any, value: any): QuestionnaireResponseAnswer[] => {
        const answers: QuestionnaireResponseAnswer[] = [];

        if (value === undefined || value === null || value === "") {
            return answers;
        }

        const toValueCoding = (val: any): QuestionnaireResponseAnswer | null => {
            if (val && typeof val === "object" && (val.code || val.system || val.display)) {
                const coding = {
                    system: val.system,
                    code: val.code ?? val.value ?? val.id ?? String(val),
                    display: val.display,
                };
                if (coding.code) return { valueCoding: coding };
                return null;
            }

            const codeStr = String(val);
            const opt = item?.answerOption?.find((opt: any) =>
                opt?.valueCoding?.code === codeStr || opt?.valueString === codeStr
            );
            if (opt?.valueCoding) {
                return { valueCoding: opt.valueCoding };
            }
            if (opt?.valueString) {
                return { valueCoding: { code: opt.valueString } };
            }
            return { valueCoding: { code: codeStr } };
        };

        switch (item.type) {
            case "boolean":
                if (typeof value === "boolean") {
                    answers.push({ valueBoolean: value });
                }
                break;

            case "decimal":
                if (typeof value === "number" || !isNaN(Number(value))) {
                    answers.push({ valueDecimal: Number(value) });
                }
                break;

            case "integer":
                if (Number.isInteger(Number(value))) {
                    answers.push({ valueInteger: Number(value) });
                }
                break;

            case "date":
                if (value && typeof value === "string") {
                    answers.push({ valueDate: value });
                }
                break;

            case "dateTime":
                if (value && typeof value === "string") {
                    answers.push({ valueDateTime: value });
                }
                break;

            case "time":
                if (value && typeof value === "string") {
                    answers.push({ valueTime: value });
                }
                break;

            case "string":
            case "text":
                if (value && typeof value === "string") {
                    answers.push({ valueString: value });
                }
                break;

            case "url":
                if (value && typeof value === "string") {
                    answers.push({ valueUri: value });
                }
                break;

            case "coding":
            case "choice":
                if (Array.isArray(value)) {
                    value.forEach((val) => {
                        const vc = toValueCoding(val);
                        if (vc) answers.push(vc);
                    });
                } else {
                    const vc = toValueCoding(value);
                    if (vc) answers.push(vc);
                }
                break;

            default:
                if (value && typeof value === "string") {
                    answers.push({ valueString: value });
                }
                break;
        }

        return answers;
    };

    const questionnaireResponse: QuestionnaireResponseData = {
        resourceType: "QuestionnaireResponse",
        meta: {
            versionId: "1",
            lastUpdated: now,
            profile: ["http://hl7.org/fhir/StructureDefinition/QuestionnaireResponse"],
            tag: [
                {
                    system: "https://fluxmed.com.br/fhir/CodeSystem/questionnaire-category",
                    code: "questionnaires",
                    display: "Questionnaires"
                },
                {
                    system: "https://fluxmed.com.br/fhir/CodeSystem/questionnaire-category",
                    code: "sono",
                    display: "Sono"
                }
            ]
        },
        questionnaire: `Questionnaire/${questionnaireId}`,
        status: "completed",
        subject: {
            reference: patientId ? `Patient/${oids.cpf}-${patientId}` : `Patient/${oids.cpf}-${subjectId}`,
        },
        authored: now,
        author: practitionerId ? {
            reference: `Practitioner/${practitionerId}`,
        } : undefined,
        item: buildResponseItems(questionnaire.item || []),
    };

    if (encounterId) {
        questionnaireResponse.encounter = {
            reference: `Encounter/${encounterId}`,
        };
    }

    // Add displayReferences if practitioner info is provided
    if (practitionerInfo?.healthcare || practitionerInfo?.practitioner) {
        const displayReferencesItem: QuestionnaireResponseItem = {
            linkId: "displayReferences",
            text: "Referências do atendimento",
            item: [
                {
                    linkId: "healthcareDisplay",
                    text: "Especialidade",
                    answer: practitionerInfo.healthcare ? [{ valueString: practitionerInfo.healthcare }] : []
                },
                {
                    linkId: "practitionerName",
                    text: "Nome do Profissional",
                    answer: practitionerInfo.practitioner ? [{ valueString: practitionerInfo.practitioner }] : []
                }
            ]
        };

        questionnaireResponse.item.push(displayReferencesItem);
    }

    return questionnaireResponse;
}

export function validateQuestionnaireResponse(
    response: QuestionnaireResponseData
): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (response.resourceType !== "QuestionnaireResponse") {
        errors.push("resourceType deve ser 'QuestionnaireResponse'");
    }

    if (!response.questionnaire) {
        errors.push("questionnaire é obrigatório");
    }

    if (!response.status) {
        errors.push("status é obrigatório");
    }

    if (!response.subject) {
        errors.push("subject é obrigatório");
    }

    if (!response.authored) {
        errors.push("authored é obrigatório");
    }

    if (!response.item || response.item.length === 0) {
        errors.push("pelo menos um item é obrigatório");
    }

    return {
        isValid: errors.length === 0,
        errors,
    };
}
